import {
  CreateModelIngestionRequest,
  CreateModelIngestionResponse,
} from '@buf/h2oai_mlops-model-ingestion.bufbuild_es/ai/h2o/mlops/ingest/v1/ingest_service_pb';
import { ModelParameters } from '@buf/h2oai_mlops-model-ingestion.bufbuild_es/ai/h2o/mlops/ingest/v1/ingestion_pb';
import { ModelIngest as ModelIngestService } from '@buf/h2oai_mlops-model-ingestion.connectrpc_es/ai/h2o/mlops/ingest/v1/ingest_service_connect';
import { Artifact } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/artifact_pb';
import {
  CreateArtifactRequest,
  CreateArtifactResponse,
  UpdateArtifactRequest,
  UpdateArtifactResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/artifact_service_pb';
import { EntityType, Metadata } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/entity_pb';
import { ExperimentParameters } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_pb';
import {
  BatchDeleteExperimentRequest,
  CreateExperimentResponse,
  DeleteExperimentRequest,
  GetExperimentRequest,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_service_pb';
import { KeySelection } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/listing_pb';
import { RegisteredModel } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_pb';
import {
  DeleteRegisteredModelRequest,
  UpdateRegisteredModelRequest,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_service_pb';
import { RegisteredModelVersion } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_pb';
import {
  CreateModelVersionRequest,
  CreateModelVersionResponse,
  DeleteModelVersionRequest,
  ListModelVersionsForModelRequest,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_service_pb';
import { ArtifactService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/artifact_service_connect';
import { ExperimentService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/experiment_service_connect';
import { RegisteredModelService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_service_connect';
import { RegisteredModelVersionService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_version_service_connect';
import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { IStyle, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesPrimary,
  buttonStylesStealth,
  ClassNamesFromIStyles,
  FileUpload,
  IH2OTheme,
  Loader,
  TextField,
  useClassNames,
  useHaicPageTitle,
  useTheme,
} from '@h2oai/ui-kit';
import { useToast } from '@h2oai/ui-kit';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { useUsers } from '../../authz/providers/UsersProvider';
import { FailedToLoadView } from '../../components/FailedToLoadView/FailedToLoadView';
import WidgetList, { WidgetListProps } from '../../components/WidgetList/WidgetList';
import { DeploymentFixed } from '../../mlops/api';
import { useMLOpsService } from '../../mlops/hooks';
import { useCloudPlatformDiscovery, useEnv } from '../../utils/hooks';
import { formatError } from '../../utils/utils';
import { ContextMenuIconButton } from '../Orchestrator/Workflows';
import { getRandomAdjective } from './adjectives';
import { FormSection } from './BatchScoringDetail';
import { useConfirmDialog } from './ConfirmDialogProvider';
import { ENDPOINTS, ROUTES } from './constants';
import { capitalize } from './DeploymentTabDetail';
import ModelMetadataView from './ModelMetadataView';
import { DeploymentsDropdown, HistoryState, ListItemDescription, RegisteredModelVersionItem } from './Models';
import ModelTaggingWidget from './ModelTaggingWidget';
import { GranularMLOpsPermissions, useGranularMLOpsPermissions } from './userActions';

export type ModelDetailNavParams = { workspace_id: string; model_id?: string };

interface IModelDetailStyles {
  form: IStyle;
  loader: IStyle;
  toastActions: IStyle;
}
export const modelDetailStyles = (theme: IH2OTheme): Partial<IModelDetailStyles> => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: 20,
    margin: '0px 10px',
  },
  loader: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 540,
    backgroundColor: theme.semanticColors?.bodyBackground,
  },
  toastActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    paddingTop: 6,
    paddingBottom: 6,
  },
});

const useColumns = ({
  onBatchScoreModelVersion,
  onDeleteModelVersion,
  onDeployModelVersion,
  onGoToDeploymentDetail,
  permissions,
}: {
  onBatchScoreModelVersion: (modelVersion: RegisteredModelVersionItem) => void;
  onDeleteModelVersion: (modelVersion: RegisteredModelVersionItem) => void;
  onDeployModelVersion: (modelVersion: RegisteredModelVersionItem) => void;
  onGoToDeploymentDetail: (deploymentId: string) => void;
  permissions: GranularMLOpsPermissions;
}) => [
  {
    key: 'spacer',
    name: '',
    minWidth: 30,
    maxWidth: 30,
    data: {
      listCellProps: {
        styles: {
          root: {
            marginLeft: 24,
          },
        },
      },
    },
  },
  {
    key: 'version',
    name: 'Version',
    fieldName: 'version',
    minWidth: 280,
    maxWidth: 280,
    data: {
      headerFieldName: 'version',
      listCellProps: {
        onRenderHeader: ({ version }: RegisteredModelVersionItem) => (
          <h4 style={{ marginRight: 0, marginTop: 0, marginBottom: 0, paddingBottom: 0 }}>{`Version ${version}`}</h4>
        ),
        onRenderText: ({ createdByName, createdTime }: RegisteredModelVersionItem) => {
          const createdTimeIso = (createdTime?.toJson() as string) || ``;
          return <ListItemDescription createdBy={createdByName} createdAt={createdTimeIso} />;
        },
      },
    },
  },
  {
    key: 'tags',
    name: '',
    minWidth: 220,
    maxWidth: 250,
    data: {
      listCellProps: {
        onRenderHeader: () => null,
        onRenderText: ({ id }: RegisteredModelVersionItem) =>
          permissions?.isAbleToViewModelTags ? (
            <ModelTaggingWidget modelVersionId={id} permissions={permissions} />
          ) : null,
      },
    },
  },
  {
    key: 'deployments',
    name: '',
    minWidth: 220,
    maxWidth: 220,
    data: {
      listCellProps: {
        onRenderHeader: () => null,
        onRenderText: ({ experimentId, workspaceId }: RegisteredModelVersionItem) =>
          permissions?.isAbleToViewDeployments ? (
            <DeploymentsDropdown
              onGoToDeploymentDetail={onGoToDeploymentDetail}
              workspaceId={workspaceId}
              experimentId={experimentId}
            />
          ) : null,
      },
    },
  },
  {
    key: 'buttons',
    name: '',
    minWidth: 200,
    data: {
      listCellProps: {
        emptyMessage: '',
        onRenderText: (modelVersion: RegisteredModelVersionItem) =>
          // TODO: Use theme prop for colors.
          permissions?.isAbleToCreateDeployments ? (
            <ContextMenuIconButton
              items={[
                ...(permissions?.isAbleToCreateDeployments ||
                permissions?.isAbleToCreateBatchScoringJobs ||
                permissions?.isAbleToDeleteModelVersion
                  ? [
                      {
                        key: 'deploy',
                        text: 'Deploy version',
                        onClick: () => {
                          onDeployModelVersion(modelVersion);
                        },
                        iconProps: { iconName: 'PublishContent', style: { color: 'var(--h2o-blue500)' } },
                        style: { color: 'var(--h2o-blue500)' },
                      },
                    ]
                  : []),
                ...(permissions?.isAbleToCreateBatchScoringJobs
                  ? [
                      {
                        key: 'batchScore',
                        text: 'Batch score',
                        onClick: () => {
                          onBatchScoreModelVersion(modelVersion);
                        },
                        iconProps: { iconName: 'AssessmentGroup', style: { color: 'var(--h2o-yellow700)' } },
                        style: { color: 'var(--h2o-yellow700)' },
                      },
                    ]
                  : []),
                ...(permissions?.isAbleToDeleteModelVersion
                  ? [
                      {
                        key: 'delete',
                        text: 'Delete version',
                        // TODO: Implement onClickDelete.
                        onClick: () => {
                          onDeleteModelVersion(modelVersion);
                        },
                        style: { color: 'var(--h2o-red400)' },
                        iconProps: {
                          iconName: 'DeleteRows',
                          style: { color: 'var(--h2o-red400)' },
                        },
                      },
                    ]
                  : []),
              ]}
            />
          ) : null,
        styles: {
          root: {
            display: 'flex',
            flexGrow: 1,
            justifyContent: 'end',
          },
        },
      },
    },
  },
];

export type ModelDetailContentProps = {
  model: RegisteredModel | null;
  loadingModel: boolean;
  workspaceId: string;
  modelId: string;
  action?: string;
  isNew: boolean;
  isNewVersionUpload: boolean;
  workspaceFQDN: string;
};

const ModelDetailContent = (props: ModelDetailContentProps) => {
  const { model, loadingModel, workspaceId, modelId, action, isNew, isNewVersionUpload, workspaceFQDN } = props;

  const history = useHistory(),
    env = useEnv(),
    theme = useTheme(),
    { showDialog } = useConfirmDialog(),
    params = { workspace_id: workspaceId, model_id: modelId, action }, // Recreate params for internal use
    classNames = useClassNames<IModelDetailStyles, ClassNamesFromIStyles<IModelDetailStyles>>(
      'modelDetail',
      modelDetailStyles(theme)
    ),
    [version, setVersion] = React.useState<number>(1),
    [modelName, setModelName] = React.useState<string>(''),
    [modelDescription, setModelDescription] = React.useState<string>(''),
    [loadingMessage, setLoadingMessage] = React.useState<string>('Loading...'),
    { addToast } = useToast(),
    permissions = useGranularMLOpsPermissions(workspaceFQDN),
    { usersNameMap } = useUsers(),
    [loading, setLoading] = React.useState(false),
    isLoading = loading || loadingModel, // Combines internal and parent loading state.
    [artifactFile, setArtifactFile] = React.useState<File | null>(null),
    [nextPageTokenVersions, setNextPageTokenVersions] = React.useState<Uint8Array>(),
    [isLoadingMoreVersions, setIsLoadingMoreVersions] = React.useState(false),
    [registeredModelVersionsItems, setRegisteredModelVersionsItems] = React.useState<RegisteredModelVersionItem[]>(),
    [deployments, setDeployments] = React.useState<DeploymentFixed[]>(),
    [uploadProgress, setUploadProgress] = React.useState<number | undefined>(undefined),
    loadStateRef = React.useRef({
      createArtifact: false,
      uploadArtifact: false,
      createModelIngestion: false,
      createExperiment: false,
      updateArtifact: false,
      registerExperimentAsModel: false,
      updateRegisteredModel: false,
      createModelVersion: false,
      countModelVersions: false,
      fetchModelVersions: false,
      deletingExperiments: false,
      fetchWorkspaceDeployments: false,
    }),
    stepsCount = isNewVersionUpload ? 6 : 8;

  React.useEffect(() => {
    if (isNew) {
      setModelName(`${capitalize(getRandomAdjective())} model`);
      setModelDescription('');
    } else if (model) {
      setModelName(model.displayName);
      setModelDescription(model.description);
    }
  }, [model, isNew]);

  const getLoadingStatusMessage = () => {
      if (loadStateRef.current.createArtifact) return `[1/${stepsCount}] Creating artifact...`;
      if (loadStateRef.current.uploadArtifact) return `[2/${stepsCount}] Uploading artifact...`;
      if (loadStateRef.current.createModelIngestion) return `[3/${stepsCount}] Ingesting model...`;
      if (loadStateRef.current.createExperiment) return `[4/${stepsCount}] Creating experiment...`;
      if (loadStateRef.current.updateArtifact) return `[5/${stepsCount}] Updating artifact...`;
      if (loadStateRef.current.createModelVersion) return '[6/6] Creating model version...';
      if (loadStateRef.current.registerExperimentAsModel) return '[6/8] Registering experiment as model...';
      if (loadStateRef.current.updateRegisteredModel) return `${isNew ? '[7/8] ' : ''}Updating registered model...`;
      if (loadStateRef.current.fetchModelVersions) return `${isNew ? '[8/8] ' : ''}Fetching model versions...`;
      if (loadStateRef.current.countModelVersions) return 'Counting model versions...';
      if (loadStateRef.current.deletingExperiments) return 'Deleting model experiments...';
      if (loadStateRef.current.fetchWorkspaceDeployments) return 'Fetching deployments...';
      return 'Loading...';
    },
    evaluateLoading = () => {
      if (
        !loadStateRef.current.createArtifact &&
        !loadStateRef.current.uploadArtifact &&
        !loadStateRef.current.createModelIngestion &&
        !loadStateRef.current.createExperiment &&
        !loadStateRef.current.updateArtifact &&
        !loadStateRef.current.registerExperimentAsModel &&
        !loadStateRef.current.updateRegisteredModel &&
        !loadStateRef.current.fetchModelVersions &&
        !loadStateRef.current.createModelVersion &&
        !loadStateRef.current.countModelVersions &&
        !loadStateRef.current.deletingExperiments &&
        !loadStateRef.current.fetchWorkspaceDeployments
      ) {
        setLoading(false);
      }
    },
    mlopsService = useMLOpsService(),
    cloudPlatformDiscovery = useCloudPlatformDiscovery(),
    mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '',
    storageTransport = React.useMemo(
      () =>
        createConnectTransport({
          baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
        }),
      [mlopsApiUrl]
    ),
    ingestTransport = React.useMemo(
      () =>
        createConnectTransport({
          baseUrl: `${mlopsApiUrl}${ENDPOINTS.ingest}/`,
        }),
      [mlopsApiUrl]
    ),
    experimentClient = React.useMemo(() => createClient(ExperimentService, storageTransport), [storageTransport]),
    artifactClient = React.useMemo(() => createClient(ArtifactService, storageTransport), [storageTransport]),
    registeredModelClient = React.useMemo(
      () => createClient(RegisteredModelService, storageTransport),
      [storageTransport]
    ),
    registerModelVersionClient = React.useMemo(
      () => createClient(RegisteredModelVersionService, storageTransport),
      [storageTransport]
    ),
    modelIngestionClient = React.useMemo(() => createClient(ModelIngestService, ingestTransport), [ingestTransport]),
    createExperiment = React.useCallback(
      async (metadata: Metadata, parameters: ModelParameters) => {
        loadStateRef.current.createExperiment = true;
        setLoading(true);
        setLoadingMessage(getLoadingStatusMessage());
        try {
          const response: CreateExperimentResponse = await experimentClient.createExperiment({
            experiment: {
              // TODO: Make sure it works even when last version is deleted and then recreated. Not supported by API yet.
              displayName: `${modelName}-v${version}`,
              description: modelDescription,
              metadata,
              parameters,
            },
            disableModelRegistration: true,
            // TODO: Use workspaceFQDN when storage is migrated.
            projectId: params.workspace_id,
          });
          return response?.experiment;
        } catch (err) {
          const message = `Failed to create experiment: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        } finally {
          loadStateRef.current.createExperiment = false;
          evaluateLoading();
        }
      },
      [addToast, params.workspace_id, modelName, modelDescription, version]
    ),
    registerExperimentAsModel = React.useCallback(
      async (experimentId: string) => {
        loadStateRef.current.registerExperimentAsModel = true;
        setLoading(true);
        setLoadingMessage(getLoadingStatusMessage());
        try {
          /*
          Registering the experiment as a model is usually a two-step process:
          1. Registering the model - RegisteredModelService.createRegisteredModel
          2. Creating a model version - RegisteredModelVersionService.createModelVersion (with the registered model id)
          When omitting "registeredModelId" from second step, a new model will be created automatically (no need to do first step).
          */
          const modelVersionBody = new CreateModelVersionRequest({
            registeredModelVersion: new RegisteredModelVersion({
              experimentId,
              version,
            }),
            registeredModelDisplayName: modelName,
            // TODO: Use workspaceFQDN when storage is migrated.
            projectId: params.workspace_id,
          });
          const response: CreateModelVersionResponse = await registerModelVersionClient.createModelVersion(
            modelVersionBody
          );
          return response?.registeredModelVersion?.registeredModelId;
        } catch (err) {
          const message = `Failed to register experiment as model: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        } finally {
          loadStateRef.current.registerExperimentAsModel = false;
          evaluateLoading();
        }
      },
      [addToast, registerModelVersionClient, params.workspace_id, modelName, version]
    ),
    createModelIngestion = React.useCallback(
      async (artifactId: string) => {
        loadStateRef.current.createModelIngestion = true;
        setLoading(true);
        setLoadingMessage(getLoadingStatusMessage());
        try {
          const modelIngestionBody = new CreateModelIngestionRequest({
            artifactId,
          });
          const response: CreateModelIngestionResponse = await modelIngestionClient.createModelIngestion(
            modelIngestionBody
          );

          if (!response.ingestion) {
            const noResponseMsg = 'No ingestion found in the response.';
            console.error(noResponseMsg);
            throw new Error(noResponseMsg);
          } else if (!response.ingestion.modelMetadata || !response.ingestion.modelParameters) {
            const noMetadataMsg = 'No model metadata or parameters found in the ingestion response.';
            console.error(noMetadataMsg);
            throw new Error(noMetadataMsg);
          } else {
            return response?.ingestion;
          }
        } catch (err) {
          const message = `Failed to ingest model: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        } finally {
          loadStateRef.current.createModelIngestion = false;
          evaluateLoading();
        }
      },
      [addToast]
    ),
    createArtifact = React.useCallback(async () => {
      loadStateRef.current.createArtifact = true;
      setLoading(true);
      setLoadingMessage(getLoadingStatusMessage());
      try {
        const artifact = new Artifact({
          entityId: params.workspace_id, // TODO: Use workspaceFQDN when storage is migrated.
          mimeType: 'application/zip',
          entityType: EntityType.PROJECT,
        });
        const artifactRequestBody = new CreateArtifactRequest({
          artifact,
        });
        const response: CreateArtifactResponse = await artifactClient.createArtifact(artifactRequestBody);
        return response?.artifact;
      } catch (err) {
        const message = `Failed to create artifact: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        return undefined;
      } finally {
        loadStateRef.current.createArtifact = false;
        evaluateLoading();
      }
    }, [addToast, params.workspace_id]),
    updateArtifact = React.useCallback(
      async (artifact: Artifact) => {
        loadStateRef.current.updateArtifact = true;
        setLoading(true);
        setLoadingMessage(getLoadingStatusMessage());
        try {
          const artifactRequestBody = new UpdateArtifactRequest({
            artifact,
            updateMask: {
              paths: ['type,entity_id'],
            },
          });
          const response: UpdateArtifactResponse = await artifactClient.updateArtifact(artifactRequestBody);
          return response?.artifact;
        } catch (err) {
          const message = `Failed to update artifact: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        } finally {
          loadStateRef.current.updateArtifact = false;
          evaluateLoading();
        }
      },
      [addToast]
    ),
    // Helper to chunk the file into Blobs.
    chunkFile = (file: File, chunkSize: number): Blob[] => {
      const chunks: Blob[] = [];
      let offset = 0;
      while (offset < file.size) {
        const chunk = file.slice(offset, offset + chunkSize);
        chunks.push(chunk);
        offset += chunkSize;
      }
      return chunks;
    },
    encodeBase64 = (uint8Array: Uint8Array): string => {
      let binary = '';
      for (let i = 0; i < uint8Array.byteLength; i++) {
        binary += String.fromCharCode(uint8Array[i]);
      }
      return btoa(binary);
    },
    // Function to stream file to Google ByteStream API.
    streamFileToGoogleByteStream = async (
      file: File,
      resourceName: string,
      baseUrl: string,
      // TODO: Revert chunk size to 1MB when the minio crash is resolved - https://github.com/h2oai/mlops-storage/issues/1626.
      chunkSize: number = 10 * 1024 * 1024 // 10MB per chunk.
    ): Promise<boolean> => {
      loadStateRef.current.uploadArtifact = true;
      setLoading(true);
      setUploadProgress(0);
      setLoadingMessage(getLoadingStatusMessage());
      const chunks = chunkFile(file, chunkSize);

      let writeOffset = 0;

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const isLastChunk = i === chunks.length - 1;

        const message = {
          resourceName,
          writeOffset: writeOffset === 0 ? undefined : writeOffset,
          finishWrite: isLastChunk,
          data: encodeBase64(new Uint8Array(await chunk.arrayBuffer())),
        };

        const requestBody = JSON.stringify(message);
        const url = new URL(`${baseUrl}/google.bytestream.ByteStream/Write`);

        let attempts = 1;
        const maxRetries = 12;

        while (attempts <= maxRetries) {
          const retryDelay = 1000 * 15 * attempts;
          try {
            const response = await fetch(url.toString(), {
              method: 'POST',
              body: requestBody,
            });

            setUploadProgress(Math.round(((i + 1) / chunks.length) * 100));

            if (response.ok) {
              if (isLastChunk) {
                setUploadProgress(undefined);
                loadStateRef.current.uploadArtifact = false;
                evaluateLoading();
                // Return true to indicate successful upload.
                return true;
              }
              writeOffset += chunk.size; // Only increment after success.
              break;
            } else {
              const errorMessage = await response.text();
              throw new Error(`HTTP error: ${response.status}, ${errorMessage}`);
            }
          } catch (error) {
            console.error(`Chunk upload attempt nr. ${attempts} failed: ${formatError(error)}`);
            attempts++;
            if (attempts >= maxRetries) {
              const message = `Error uploading chunk at offset ${writeOffset}: ${formatError(error)}`;
              addToast({
                messageBarType: MessageBarType.error,
                message,
              });
              throw error;
            }
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          }
        }
      }

      // If we reach here, it means the upload was not successful.
      setUploadProgress(undefined);
      loadStateRef.current.uploadArtifact = false;
      evaluateLoading();
      // Return false to indicate failed upload.
      return false;
    },
    listModelVersionsForModel = React.useCallback(
      async (registeredModelId: string, modelName: string) => {
        loadStateRef.current.fetchModelVersions = true;
        try {
          const listModelVersionsForModelBody = new ListModelVersionsForModelRequest({
            registeredModelId,
          });
          const response = await registerModelVersionClient.listModelVersionsForModel(listModelVersionsForModelBody);
          const modelVersions = response?.modelVersions;
          if (!modelVersions) console.error('No model versions found in the response.');
          return modelVersions;
        } catch (err) {
          const message = `Failed to fetch model versions for ${modelName} (${registeredModelId}): ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        } finally {
          loadStateRef.current.fetchModelVersions = false;
          evaluateLoading();
        }
      },
      [addToast, registerModelVersionClient]
    ),
    updateRegisteredModel = React.useCallback(
      async (registeredModel: RegisteredModel) => {
        loadStateRef.current.updateRegisteredModel = true;
        setLoading(true);
        setLoadingMessage(getLoadingStatusMessage());
        try {
          const updateRegisteredModelBody = new UpdateRegisteredModelRequest({
            registeredModel,
            updateMask: {
              paths: isNew ? ['description'] : ['description', 'name'],
            },
          });
          await registeredModelClient.updateRegisteredModel(updateRegisteredModelBody);
          const modelVersions = await listModelVersionsForModel(registeredModel.id, registeredModel.displayName);
          const currentVersion = modelVersions?.find(
            (modelVersion) => modelVersion.registeredModelId === registeredModel.id
          );
          addToast({
            messageBarType: MessageBarType.success,
            message: `Model "${modelName}" ${isNew ? 'added' : 'updated'} successfully.`,
            timeout: 10000,
            actions:
              currentVersion?.id &&
              (permissions?.isAbleToCreateBatchScoringJobs || permissions?.isAbleToCreateDeployments) ? (
                <div className={classNames.toastActions}>
                  {permissions?.isAbleToCreateBatchScoringJobs ? (
                    <Button
                      text="Batch score now"
                      styles={buttonStylesStealth}
                      onClick={() =>
                        history.push(
                          `/mlops/${workspaceFQDN}${ROUTES.BATCH_SCORING}/start-new/from-model/${currentVersion?.id}`
                        )
                      }
                    />
                  ) : null}
                  {permissions?.isAbleToCreateDeployments ? (
                    <Button
                      text="Deploy now"
                      styles={buttonStylesStealth}
                      onClick={() =>
                        history.push(
                          `/mlops/${workspaceFQDN}${ROUTES.DEPLOYMENTS}/create-new/deploy/${currentVersion?.id}`
                        )
                      }
                    />
                  ) : null}
                </div>
              ) : undefined,
          });
          history.goBack();
        } catch (err) {
          const message = `Failed to update model: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          loadStateRef.current.updateRegisteredModel = false;
          evaluateLoading();
        }
      },
      [addToast, modelName, isNew, permissions?.isAbleToCreateBatchScoringJobs, permissions?.isAbleToCreateDeployments]
    ),
    createModelVersion = React.useCallback(
      async (registeredModelId: string, experimentId: string) => {
        loadStateRef.current.createModelVersion = true;
        setLoading(true);
        setLoadingMessage(getLoadingStatusMessage());
        try {
          const modelVersionBody = new CreateModelVersionRequest({
            registeredModelVersion: new RegisteredModelVersion({
              registeredModelId,
              experimentId,
              version,
            }),
            registeredModelDisplayName: modelName,
            // TODO: Use workspaceFQDN when storage is migrated.
            projectId: params.workspace_id,
          });
          const response = await registerModelVersionClient.createModelVersion(modelVersionBody);
          addToast({
            messageBarType: MessageBarType.success,
            message: `Version "${version}" of "${modelName}" was added successfully.`,
            timeout: 10000,
            actions:
              response.registeredModelVersion?.id &&
              (permissions?.isAbleToCreateBatchScoringJobs || permissions?.isAbleToCreateDeployments) ? (
                <div className={classNames.toastActions}>
                  {permissions.isAbleToCreateBatchScoringJobs ? (
                    <Button
                      text="Batch score now"
                      styles={buttonStylesStealth}
                      onClick={() =>
                        history.push(
                          `/mlops/${workspaceFQDN}${ROUTES.BATCH_SCORING}/start-new/from-model/${response.registeredModelVersion?.id}`
                        )
                      }
                    />
                  ) : null}
                  {permissions.isAbleToCreateDeployments ? (
                    <Button
                      text="Deploy now"
                      styles={buttonStylesStealth}
                      onClick={() =>
                        history.push(
                          `/mlops/${workspaceFQDN}${ROUTES.DEPLOYMENTS}/create-new/deploy/${response.registeredModelVersion?.id}`
                        )
                      }
                    />
                  ) : null}
                </div>
              ) : undefined,
          });
          if ((history?.location?.state as HistoryState)?.from === 'model-selector') {
            history.push(
              `/mlops/${workspaceFQDN}${ROUTES.DEPLOYMENTS}/create-new/deploy/${response.registeredModelVersion?.id}`
            );
          } else {
            history.goBack();
          }
          // If history should navigate to model detail, re-fetch model versions.
          if ((history?.location?.state as HistoryState)?.from !== 'models') {
            void getRegisteredModelVersions(params.model_id);
          }
        } catch (err) {
          const message = `Failed to create model version: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          loadStateRef.current.createModelVersion = false;
          evaluateLoading();
        }
      },
      [
        addToast,
        modelName,
        params.workspace_id,
        version,
        permissions?.isAbleToCancelBatchScoringJobs,
        permissions?.isAbleToCreateDeployments,
      ]
    ),
    uploadArtifactFile = React.useCallback(
      async (file: File) => {
        const artifact = await createArtifact();
        const artifactId = artifact?.id;
        if (!artifactId) return;

        const baseUrl = `${mlopsApiUrl}/storage`;
        const isUploadSuccessful = await streamFileToGoogleByteStream(file, artifactId, baseUrl);
        if (!isUploadSuccessful) {
          addToast({
            messageBarType: MessageBarType.error,
            message: 'Failed to upload artifact file.',
          });
          return;
        }

        const ingestion = await createModelIngestion(artifactId);
        if (!ingestion || !ingestion.modelMetadata || !ingestion.modelParameters) return;
        const experiment = await createExperiment(
          new Metadata(ingestion.modelMetadata),
          new ExperimentParameters(ingestion.modelParameters)
        );
        if (!experiment) return;
        const updatedArtifact = await updateArtifact(
          new Artifact({
            id: artifactId,
            type: ingestion.artifactType,
            entityId: experiment.id,
            entityType: EntityType.EXPERIMENT,
          })
        );
        if (!updatedArtifact) return;
        if (isNewVersionUpload) {
          await createModelVersion(params.model_id, experiment.id);
        } else {
          const registeredModelId = await registerExperimentAsModel(experiment.id);
          if (!registeredModelId) return;
          const registeredModel = new RegisteredModel({
            id: registeredModelId,
            description: modelDescription,
            // TODO: Use workspaceFQDN when storage is migrated.
            projectId: params.workspace_id,
          });
          await updateRegisteredModel(registeredModel);
        }
      },
      [
        addToast,
        createArtifact,
        createModelIngestion,
        createExperiment,
        updateArtifact,
        registerExperimentAsModel,
        createModelVersion,
        updateRegisteredModel,
        params.workspace_id,
        params.model_id,
        modelDescription,
        isNewVersionUpload,
        mlopsApiUrl,
      ]
    ),
    onUploadModel = React.useCallback(
      (files: File[]) => {
        if (files && files.length) {
          setArtifactFile(files[0]);
        } else {
          setArtifactFile(null);
        }
      },
      [uploadArtifactFile]
    ),
    onClickAddModel = React.useCallback(() => {
      if (isNew || isNewVersionUpload) {
        if (artifactFile) uploadArtifactFile(artifactFile);
      } else {
        void updateRegisteredModel(
          new RegisteredModel({
            id: params.model_id,
            displayName: modelName,
            description: modelDescription,
            // TODO: Use workspaceFQDN when storage is migrated.
            projectId: params.workspace_id,
          })
        );
      }
    }, [
      artifactFile,
      uploadArtifactFile,
      updateRegisteredModel,
      modelName,
      modelDescription,
      isNew,
      params.model_id,
      params.workspace_id,
      isNewVersionUpload,
    ]),
    batchDeleteExperiment = React.useCallback(
      async (experimentIds: string[]) => {
        try {
          if (!params.workspace_id) return;
          loadStateRef.current.deletingExperiments = true;
          const deleteExperimentsBody = new BatchDeleteExperimentRequest({
            experimentRequest: experimentIds.map(
              (id) =>
                new DeleteExperimentRequest({
                  id,
                  // TODO: Use workspaceFQDN when storage is migrated.
                  projectId: params.workspace_id,
                })
            ),
          });
          await experimentClient.batchDeleteExperiment(deleteExperimentsBody);
        } catch (err) {
          const message = `Failed to delete model experiments: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          loadStateRef.current.deletingExperiments = false;
          evaluateLoading();
        }
      },
      [addToast, params.workspace_id]
    ),
    deleteModel = React.useCallback(
      async (modelId: string, relatedExperimentIds?: string[]) => {
        try {
          const deleteRegisteredModelBody = new DeleteRegisteredModelRequest({
            modelId,
          });
          await registeredModelClient.deleteRegisteredModel(deleteRegisteredModelBody);
          if (relatedExperimentIds) await batchDeleteExperiment(relatedExperimentIds);
          addToast({
            messageBarType: MessageBarType.success,
            message: 'Model deleted successfully.',
          });
          history.goBack();
        } catch (err) {
          const message = `Failed to delete model: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        }
      },
      [addToast, workspaceFQDN, history]
    ),
    deleteModelVersion = React.useCallback(
      async (modelVersionId: string, experimentId: string) => {
        try {
          const deleteModelVersionBody = new DeleteModelVersionRequest({
            id: modelVersionId,
          });
          await registerModelVersionClient.deleteModelVersion(deleteModelVersionBody);
          if (experimentId) await batchDeleteExperiment([experimentId]);
          addToast({
            messageBarType: MessageBarType.success,
            message: 'Model version deleted successfully.',
          });
          await getRegisteredModelVersions(params.model_id);
        } catch (err) {
          const message = `Failed to delete model version: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        }
      },
      [addToast, workspaceFQDN, params.model_id]
    ),
    getRegisteredModelVersions = React.useCallback(
      async (registeredModelId: string, pageToken?: Uint8Array) => {
        loadStateRef.current.countModelVersions = true;

        if (pageToken) {
          setIsLoadingMoreVersions(true);
        } else {
          setLoading(true);
          setLoadingMessage(getLoadingStatusMessage());
        }
        try {
          const response = await registerModelVersionClient.listModelVersionsForModel(
            new ListModelVersionsForModelRequest({ registeredModelId })
          );
          const modelVersions = response?.modelVersions;
          if (!modelVersions) console.error('No model versions found in the response.');

          const modelUsersIds: string[] = [];
          if (modelVersions) {
            modelVersions.forEach((modelVersion) => {
              if (modelVersion.createdBy) modelUsersIds.push(modelVersion.createdBy);
            });
          }

          setNextPageTokenVersions(response?.paging?.nextPageToken);

          const sortedModelVersions = modelVersions.sort(
            (a, b) => Number(a.createdTime?.seconds || 0) - Number(b.createdTime?.seconds || 0)
          );

          const allVersionsMetadata = await Promise.all(
            sortedModelVersions.map((modelVersion) => getExperimentMetadata(modelVersion.experimentId))
          );

          const newItems = sortedModelVersions.map(
            (modelVersion, idx) =>
              ({
                ...modelVersion,
                createdByName: usersNameMap?.[`users/${modelVersion.createdBy}`]?.displayName || modelVersion.createdBy,
                hasLeftOpenClose: true,
                collapsed: true,
                contents: (
                  <ModelMetadataView
                    experimentId={modelVersion.experimentId}
                    metadata={allVersionsMetadata?.[idx]?.metadata}
                    parameters={allVersionsMetadata?.[idx]?.parameters}
                  />
                ),
                modelVersionId: modelVersion.id,
                workspaceId: params.workspace_id,
              } as unknown as RegisteredModelVersionItem)
          );
          setRegisteredModelVersionsItems((items) => (pageToken ? [...(items || []), ...(newItems || [])] : newItems));
          // Find the one with the highest version number.
          const count = modelVersions.reduce((max, modelVersion) => {
            const version = modelVersion.version;
            return version > max ? version : max;
          }, 0);
          setVersion(count ? count + 1 : 1);
        } catch (err) {
          const message = `Failed to get registered model versions: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          setRegisteredModelVersionsItems(undefined);
        } finally {
          loadStateRef.current.countModelVersions = false;
          evaluateLoading();
          setIsLoadingMoreVersions(false);
        }
      },
      [addToast, deployments, usersNameMap, permissions]
    ),
    fetchDeployments = React.useCallback(async () => {
      if (!workspaceFQDN) return;
      loadStateRef.current.fetchWorkspaceDeployments = true;
      setLoading(true);
      try {
        const deploymentItems: DeploymentFixed[] = [];
        let pageToken: string | undefined;

        do {
          const response = await mlopsService.listDeployments({
            parent: workspaceFQDN,
            pageSize: 20,
            orderBy: 'create_time desc',
            pageToken,
          });

          if (response?.deployments) {
            deploymentItems.push(...response.deployments);
          }

          pageToken = response?.nextPageToken;
        } while (pageToken);

        if (deploymentItems.length === 0) {
          console.error('No deployments found in the response.');
        }

        setDeployments(deploymentItems);
      } catch (err) {
        const message = `Failed to fetch deployments: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        setDeployments(undefined);
      } finally {
        loadStateRef.current.fetchWorkspaceDeployments = false;
        evaluateLoading();
      }
    }, [addToast, workspaceFQDN]),
    onDeleteModel = React.useCallback(() => {
      showDialog?.({
        title: 'Delete model?',
        description: 'This will delete the model and all its versions. This action cannot be undone.',
        onConfirm: () => {
          // Setter is used to avoid stale closure.
          setRegisteredModelVersionsItems((versionItems) => {
            const modelExperiments = (versionItems || []).map((modelVersion) => modelVersion.experimentId);
            deleteModel(params.model_id, modelExperiments);
            return versionItems;
          });
        },
      });
    }, [params.model_id, deleteModel]),
    getExperimentMetadata = React.useCallback(
      async (experimentId: string) => {
        try {
          const response = await experimentClient.getExperiment(
            new GetExperimentRequest({
              id: experimentId,
              responseMetadata: new KeySelection({
                // TODO: Get these from service instead of hardcoding once available.
                pattern: [
                  'source',
                  'score',
                  'dai/score',
                  'scorer',
                  'dai/scorer',
                  'test_score',
                  'dai/test_score',
                  'validation_score',
                  'dai/validation_score',
                  'tool_version',
                  'dai/tool_version',
                  'model_parameters',
                  'dai/model_parameters',
                  'model_type',
                  'tool',
                  'mlflow/flavors/python_function/loader_module',
                  'input_schema',
                  'output_schema',
                ],
              }),
            })
          );
          const experiment = response?.experiment;
          if (!experiment) console.error('No experiment found in the response.');
          return { metadata: experiment?.metadata, parameters: experiment?.parameters };
        } catch (err) {
          const message = `Failed to fetch experiment metadata: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return { metadata: undefined, parameters: undefined };
        }
      },
      [addToast]
    ),
    columns = useColumns({
      onBatchScoreModelVersion: (modelVersion: RegisteredModelVersionItem) => {
        history.push(`/mlops/${workspaceFQDN}${ROUTES.BATCH_SCORING}/start-new/from-model/${modelVersion.id}`);
      },
      onDeleteModelVersion: (modelVersion: RegisteredModelVersionItem) => {
        showDialog?.({
          title: 'Delete model version?',
          description: `This will delete version ${modelVersion.version} of the model. This action cannot be undone.`,
          onConfirm: () => deleteModelVersion(modelVersion.id, modelVersion.experimentId),
        });
      },
      onDeployModelVersion: (modelVersion: RegisteredModelVersionItem) => {
        history.push(`/mlops/${workspaceFQDN}${ROUTES.DEPLOYMENTS}/create-new/deploy/${modelVersion.id}`);
      },
      onGoToDeploymentDetail: (deploymentId: string) => {
        window.open(`/mlops/${workspaceFQDN}${ROUTES.DEPLOYMENTS}/${deploymentId || ''}`, '_blank');
      },
      permissions,
    }),
    widgetListProps: WidgetListProps = {
      columns,
      items: registeredModelVersionsItems,
      loading: isLoading,
      delayLoader: false,
      isLoadingMore: isLoadingMoreVersions,
      onLoadMore: nextPageTokenVersions
        ? () => void getRegisteredModelVersions(params.model_id, nextPageTokenVersions)
        : undefined,
      NoItemsContent: <div></div>,
      ErrorContent: FailedToLoadView({
        title: 'Failed to load model versions',
        description: 'Please try again later. If the problem persists, contact our support.',
        actionTitle: 'Retry',
        onActionClick: () => void getRegisteredModelVersions(params.model_id),
        actionIcon: 'Refresh',
      }),
      compact: true,
    };

  React.useEffect(() => {
    if (params.model_id && params.model_id !== 'add-new') {
      if (!deployments) {
        if (workspaceFQDN) void fetchDeployments();
      } else {
        void getRegisteredModelVersions(params.model_id);
      }
    }
  }, [params.workspace_id, params.model_id, getRegisteredModelVersions, deployments, workspaceFQDN]);

  useHaicPageTitle(
    uploadProgress
      ? `${uploadProgress}% uploaded | Add Model | MLOps`
      : `${isNew || isNewVersionUpload ? 'Add Model' : modelName || 'Model Details'} | MLOps`,
    env?.cloudInstanceName
  );

  return (
    <>
      <div className={classNames.form}>
        {isLoading ? (
          <div className={classNames.loader}>
            <Loader label={`${loadingMessage}${uploadProgress !== undefined ? ` (${uploadProgress}%)` : ''}`} />
          </div>
        ) : (
          <>
            <TextField
              label="Name"
              required
              value={modelName}
              onChange={(_ev, newValue) => setModelName(newValue || '')}
              readOnly={!(permissions?.isAbleToCreateModel || permissions?.isAbleToUpdateModel)}
              disabled={isNewVersionUpload}
              styles={{ root: { maxWidth: 360 } }}
            />
            <TextField
              label="Description"
              value={modelDescription}
              onChange={(_ev, newValue) => setModelDescription(newValue || '')}
              readOnly={!(permissions?.isAbleToCreateModel || permissions?.isAbleToUpdateModel)}
              disabled={isNewVersionUpload}
              styles={{ root: { maxWidth: 360 } }}
            />
            {isNew || isNewVersionUpload ? (
              <TextField label="Version" value={`${version}`} readOnly disabled styles={{ root: { maxWidth: 360 } }} />
            ) : null}
            {!(permissions?.isAbleToCreateModel || permissions?.isAbleToUpdateModel) ? null : (
              <>
                {isNew || isNewVersionUpload ? (
                  <FileUpload
                    fileExtensions={['.zip']}
                    label="Model file"
                    uploadCallback={onUploadModel}
                    styles={{ root: { margin: '10px 0' } }}
                  />
                ) : null}
                <Button
                  text={isNew ? 'Add model' : isNewVersionUpload ? 'Add new version' : 'Save changes'}
                  primary
                  style={isNew ? undefined : { margin: '10px 0' }}
                  styles={buttonStylesPrimary}
                  disabled={isNew || isNewVersionUpload ? !modelName || !artifactFile : !modelName}
                  onClick={onClickAddModel}
                />
              </>
            )}
            {!isNew && !isNewVersionUpload ? (
              <>
                <FormSection title="Model versions">
                  {registeredModelVersionsItems?.length === 0 ? <p>No model versions found for this model.</p> : null}
                  {permissions?.isAbleToCreateModelVersion ? (
                    <>
                      <Button
                        text="Add new model version"
                        iconName="Add"
                        styles={buttonStylesStealth}
                        onClick={() =>
                          history.push(`/mlops/${workspaceFQDN}${ROUTES.MODELS}/${params.model_id}/add-new-version`)
                        }
                      />
                      <div style={{ height: 20 }} />
                    </>
                  ) : null}
                  <WidgetList {...widgetListProps} />
                </FormSection>
                {permissions?.isAbleToDeleteModel ? (
                  <>
                    <div style={{ height: 20 }} />
                    <Button
                      text="Delete model"
                      onClick={onDeleteModel}
                      style={{
                        color: 'var(--h2o-red400)',
                        borderColor: 'var(--h2o-red400)',
                      }}
                      iconProps={{ iconName: 'Delete', style: { color: 'var(--h2o-red400)' } }}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </>
        )}
      </div>
    </>
  );
};

export default ModelDetailContent;
